import { useEffect, useState } from 'react';
import { featureAdoptionLib } from 'shared/libs/feature-adoption';
import { FeatureAdoptionTypes } from '../domain';
import { orderDetailAdoptionService } from '../services';
import { useOrderDetailAdoption } from './useOrderDetailAdoption';

import type { OrderDetailState } from 'shared/domain';
import type { AdoptionStep } from 'shared/libs/feature-adoption';
import type { FeatureAdoptionData } from '../domain';

// TODO: Remove this functionality after 3 months, launch date 24/Sep/24.
/* Removal date: 24/Dec/24.
  This is part of a feature adoption experiment and should be revisited 
  to determine if it should be permanently integrated or deprecated.
  Related items: RA-4020, RA-4023, RA-4251
 */
export function useOrderDetailDialog(
  isOpenDialog: boolean,
  currentOrder: OrderDetailState,
) {
  const [steps, setSteps] = useState<AdoptionStep[]>([]);

  const { destroyAdoption, startAdoption, tooltip } = featureAdoptionLib();
  const {
    getStoredData,
    updateStoredData,
    hasDayElapsed,
    addInitialStoredData,
    addStoredData,
    hasHandoff,
    isPrintUnset,
    MAX_VIEWS,
    partnerId,
    storeId,
  } = orderDetailAdoptionService(currentOrder);
  const { step } = useOrderDetailAdoption({ partnerId, storeId, tooltip });

  useEffect(() => {
    if (!isOpenDialog) {
      setSteps([]);
      return;
    }
    const data = getStoredData();
    if (data.length > 0) {
      updateStoredData(data);
      _prepare(data);
    } else {
      addInitialStoredData();
      _setup();
    }
  }, [isOpenDialog]);

  useEffect(() => {
    if (isOpenDialog && steps.length > 0) {
      startAdoption(steps);
    }
    return () => {
      destroyAdoption();
    };
  }, [isOpenDialog, steps]);

  const _setup = () => {
    const steps: AdoptionStep[] = [
      ...(isPrintUnset ? [step.printed] : []),
      ...(hasHandoff ? [step.handoff] : []),
    ];
    setSteps(steps);
  };

  const _prepare = (data: FeatureAdoptionData[]) => {
    let items: AdoptionStep[] = [];
    const hasPrintedAdoptionInfo = data.some(
      val => val.type === FeatureAdoptionTypes.ScalePrinted,
    );
    const hasInputBagsDrinksInfo = data.some(
      val => val.type === FeatureAdoptionTypes.handoff,
    );

    if (!hasPrintedAdoptionInfo && isPrintUnset) {
      items = [...items, step.printed];
      addStoredData(FeatureAdoptionTypes.ScalePrinted);
    }
    items = [
      ...items,
      ...data
        .filter(
          value => value.views < MAX_VIEWS || hasDayElapsed(value.lastShown),
        )
        .map(value => _prepareStep(value.type))
        .filter((item): item is AdoptionStep => item !== undefined),
    ];

    if (!hasInputBagsDrinksInfo && hasHandoff) {
      items = [...items, step.handoff];
      addStoredData(FeatureAdoptionTypes.handoff);
    }

    setSteps(items);
  };

  const _prepareStep = (type: FeatureAdoptionTypes) => {
    if (type === FeatureAdoptionTypes.ScalePrinted && isPrintUnset) {
      return step.printed;
    } else if (type === FeatureAdoptionTypes.handoff && hasHandoff) {
      return step.handoff;
    }
  };
}
